import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { ShieldCheck, FileText, Mail, Clock, CheckCircle2, XCircle } from "lucide-react";
import { api, V1 } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function SchoolClaimButton({ school }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ official_email: "", document_url: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null); // {status: 'owner'|'pending'|'approved'|'rejected'|'none', claim}

  const loadStatus = async () => {
    if (!user || !school?.id) { setStatus(null); return; }
    try {
      const { data } = await api.get(`${V1}/schools/${school.id}/my-claim-status`);
      setStatus(data);
    } catch { setStatus({ status: "none", claim: null }); }
  };

  useEffect(() => { loadStatus(); /* eslint-disable-next-line */ }, [user?.id, school?.id]);

  if (!school) return null;
  // If user already owns this school, don't show button
  if (status?.status === "owner") return null;

  const isPending = status?.status === "pending";
  const isRejected = status?.status === "rejected";

  const handleClick = () => {
    if (!user) {
      toast.info("Sahiplik talebi için giriş yapmalısın");
      navigate("/giris", { state: { from: `/okul/${school.slug}` } });
      return;
    }
    if (isPending) return; // no-op — button is locked
    setOpen(true);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        school_id: school.id,
        official_email: form.official_email.trim(),
      };
      if (form.document_url.trim()) payload.document_url = form.document_url.trim();
      if (form.note.trim()) payload.note = form.note.trim();
      await api.post(`${V1}/schools/${school.id}/claim`, payload);
      toast.success("Sahiplik talebiniz alındı. Yönetici incelemesinden sonra bildirim alacaksınız.");
      setOpen(false);
      setForm({ official_email: "", document_url: "", note: "" });
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Talep gönderilemedi");
    } finally { setBusy(false); }
  };

  // Render locked "Talebiniz İncelemede" state
  if (isPending) {
    return (
      <Button
        variant="outline"
        size="sm"
        disabled
        className="cursor-not-allowed opacity-90 bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/20 dark:border-amber-700 dark:text-amber-200"
        data-testid="claim-pending-btn"
        title={`Talebiniz ${new Date(status.claim.created_at).toLocaleDateString('tr-TR')} tarihinde alındı`}
      >
        <Clock className="h-4 w-4 animate-pulse" /> Talebiniz İncelemede
      </Button>
    );
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={handleClick}
        data-testid="claim-school-btn"
      >
        {isRejected ? (
          <><XCircle className="h-4 w-4" /> Tekrar Talep Et</>
        ) : (
          <><ShieldCheck className="h-4 w-4" /> Bu Okulu Yönetiyorum</>
        )}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="claim-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-accent" /> Okul Sahiplik Talebi
            </DialogTitle>
            <DialogDescription>
              <b>{school.name}</b> için yönetim yetkisi talep ediyorsunuz. Doğrulama için resmi
              e-posta (@meb.k12.tr / @edu.tr) veya MEB belgesi paylaşın.
            </DialogDescription>
          </DialogHeader>
          {isRejected && (
            <div className="p-3 rounded-lg border border-destructive/40 bg-destructive/10 text-sm text-destructive">
              <b>Önceki talebiniz reddedildi.</b>
              {status?.claim?.rejection_reason && <div className="mt-1 text-xs">Sebep: {status.claim.rejection_reason}</div>}
            </div>
          )}
          <div className="space-y-3">
            <div>
              <Label className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> Resmi Kurum E-postası *</Label>
              <Input
                type="email"
                value={form.official_email}
                onChange={(e) => setForm((s) => ({ ...s, official_email: e.target.value }))}
                placeholder="mudur@okulunuz.k12.tr"
                data-testid="claim-email"
              />
              <div className="text-[11px] text-muted-foreground mt-1">
                @meb.k12.tr / @edu.tr / @k12.tr uzantısı hızlı onay sağlar.
              </div>
            </div>
            <div>
              <Label className="flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> MEB İzin Belgesi Bağlantısı</Label>
              <Input
                value={form.document_url}
                onChange={(e) => setForm((s) => ({ ...s, document_url: e.target.value }))}
                placeholder="https://drive.google.com/..."
                data-testid="claim-doc"
              />
              <div className="text-[11px] text-muted-foreground mt-1">
                Resmi e-postanız kurum uzantılı değilse belge yüklemeniz gerekir.
              </div>
            </div>
            <div>
              <Label>Not (opsiyonel)</Label>
              <Textarea
                rows={3}
                value={form.note}
                onChange={(e) => setForm((s) => ({ ...s, note: e.target.value }))}
                placeholder="Ek açıklama..."
                data-testid="claim-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button>
            <Button
              onClick={submit}
              disabled={busy || !form.official_email}
              data-testid="claim-submit-btn"
            >
              <ShieldCheck className="h-4 w-4" /> {busy ? "Gönderiliyor…" : "Sahiplik Talebini Gönder"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
